import {Component, DestroyRef, inject, OnInit} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {ActivatedRoute, Router} from '@angular/router';
import {DatePipe, Location, NgClass} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {catchError, forkJoin, of} from 'rxjs';
import {HttpErrorResponse} from '@angular/common/http';
import {MatProgressSpinner} from '@angular/material/progress-spinner';
import {MatIcon} from '@angular/material/icon';
import {MatButton, MatIconButton} from '@angular/material/button';
import {MatTooltip} from '@angular/material/tooltip';
import {MatFormField, MatInput, MatLabel, MatSuffix} from '@angular/material/input';
import {BenchOverviewService} from '../../../services/bench-overview.service';
import {CompanyService} from '../../../services/company.service';
import {
  BenchEntryDTO, BusinessUnitDTO, isBenchFirm, NoteDTO, OfferDTO, REQUEST_STATUSES, RequestStatus, SalesPersonDTO,
} from '../../../shared/model/bench-entry.model';
import {BuBadgeComponent} from '../../../shared/components/bu-badge/bu-badge.component';
import {ExtensionBadgeComponent} from '../../../shared/components/extension-badge/extension-badge.component';
import {MessageService} from '../../../shared/components/message.service';
import {LazyImageDirective} from '../../../shared/lazy-image.directive';
import {sortIndicatorFor} from '../../../shared/sort-indicator';
import {CompanyBadgeComponent} from '../../../shared/components/company-badge/company-badge.component';
import {CiMultiselectComponent} from '../../../../profiler-theme';
import {ModalComponent} from '../../../shared/components/modal/modal.component';
import {
  buildSalesIndex, salesAbbreviations, SalesIndex, SalesSubstitution, SalesSubstitutionService,
} from '../../../services/sales-substitution.service';
import {
  SALES_PROBABILITIES,
  SALES_SORT_KEYS,
  SalesFilterState,
  SalesFilterStateService,
} from '../../../services/sales-filter-state.service';
import {pathOf, replaceQueryParams} from '../../../shared/replace-query-params';

/** Gewählte Werte in der Reihenfolge der Optionen; nicht (mehr) angebotene Werte bleiben dahinter stehen. */
function inOptionOrder(selected: string[], options: readonly string[]): string[] {
  return [...options.filter(o => selected.includes(o)), ...selected.filter(s => !options.includes(s))];
}

/** One row of the sales view: an offer together with the person it belongs to. */
interface SalesRow {
  entry: BenchEntryDTO;
  offer: OfferDTO;
  /** Wer das Angebot gerade betreut (NP20-924) — einmal aufgelöst, von Anzeige und Sortierung genutzt. */
  submitter: SalesSubstitution;
  /** Transient input for adding a comment to this offer (NP20-798). */
  commentDraft: string;
  /** Whether the offer's comment list + input is expanded (NP20-798). */
  commentsOpen: boolean;
}

/** Sales-View: listet alle Angebote flach, filter- und sortierbar (NP20-778). */
@Component({
  selector: 'app-sales-view',
  imports: [
    DatePipe,
    NgClass,
    FormsModule,
    TranslatePipe,
    MatProgressSpinner,
    MatIcon,
    MatButton,
    MatIconButton,
    MatTooltip,
    MatFormField,
    MatInput,
    MatLabel,
    MatSuffix,
    LazyImageDirective,
    BuBadgeComponent,
    ExtensionBadgeComponent,
    CompanyBadgeComponent,
    CiMultiselectComponent,
    ModalComponent,
  ],
  templateUrl: './sales-view.component.html',
  styleUrl: './sales-view.component.scss',
  standalone: true,
})
export class SalesViewComponent implements OnInit {
  private readonly benchService = inject(BenchOverviewService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);
  private readonly messageService = inject(MessageService);
  private readonly translate = inject(TranslateService);
  private readonly companyService = inject(CompanyService);
  private readonly substitution = inject(SalesSubstitutionService);
  private readonly route = inject(ActivatedRoute);
  private readonly location = inject(Location);
  private readonly filterState = inject(SalesFilterStateService);

  private static readonly URL_WRITE_DELAY_MS = 300;
  private urlWriteTimer?: number;
  private ownPath = '';

  companyName(abbreviation: string | null | undefined): string {
    return this.companyService.byAbbreviation(abbreviation ?? undefined)?.name ?? (abbreviation ?? '');
  }

  submittedByOptions: string[] = [];
  isLoading = true;

  /** Template-Guard: Firma-Badge nur bei bekannter Firma rendern (NP20-877). */
  protected readonly isBenchFirm = isBenchFirm;

  /** Leere Liste = alle; innerhalb eines Filters oder, zwischen den Filtern und (NP20-1283). */
  submittedByFilter: string[] = [];
  probabilityFilter: string[] = [];
  buFilter: string[] = [];
  searchText = '';
  // Filterliste: alle vorkommenden Werte, anders als die anklickbaren der Detailansicht (NP20-1184).
  get probabilityOptions(): readonly string[] {
    return SALES_PROBABILITIES;
  }
  businessUnits: BusinessUnitDTO[] = [];
  buById = new Map<string, BusinessUnitDTO>();

  submittedByFilterOptions: string[] = [];
  probabilityFilterOptions: string[] = [];
  buFilterOptions: string[] = [];
  private buFilterLabels = new Map<string, string>();

  displayProbabilityFilter = (value: string): string => `${value} %`;

  displayBuFilter = (value: string): string => this.buFilterLabels.get(value) ?? value;

  // Per-offer comments (NP20-798): a comment line sits under every offer row (like the
  // meeting-mode note). Adding/removing writes to offer.notes and persists via updateEntry.
  savingOffer: OfferDTO | null = null;
  pendingCommentRemoval: {row: SalesRow; note: NoteDTO} | null = null;

  sortKey = 'person';
  sortDir: 'asc' | 'desc' = 'asc';
  // Getter statt Feld: importierte Konstanten sind in Feld-Initializern im Vitest-Gesamtlauf undefined.
  get sortColumns(): {key: string; labelKey: string}[] {
    return SALES_SORT_KEYS.map(key => ({key, labelKey: `salesView.col.${key}`}));
  }

  private rows: SalesRow[] = [];
  visibleRows: SalesRow[] = [];
  private salesIndex: SalesIndex = buildSalesIndex([]);

  ngOnInit(): void {
    this.ownPath = pathOf(this.location.path(true));
    this.destroyRef.onDestroy(() => window.clearTimeout(this.urlWriteTimer));
    this.restoreFilterState();
    this.buildFilterOptions();
    this.translate.onLangChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.buildFilterOptions());
    this.companyService.load().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
    // Entries drive the list; the sales list resolves the active substitution and fills the
    // offering-person filter. A sales-endpoint failure must not block the view (catchError →
    // empty): dann stehen die gespeicherten Kürzel ohne Vertretung, kein Angebot verschwindet.
    forkJoin({
      entries: this.benchService.getEntries(),
      sales: this.benchService.getSales().pipe(catchError(() => of(null))),
      businessUnits: this.benchService.getBusinessUnits().pipe(catchError(() => of([] as BusinessUnitDTO[]))),
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({entries, sales: loadedSales, businessUnits}) => {
          const sales: SalesPersonDTO[] = loadedSales ?? [];
          this.salesIndex = buildSalesIndex(sales);
          this.businessUnits = businessUnits;
          this.buById = new Map(businessUnits.map(b => [b.id, b]));
          this.rows = entries.flatMap(entry =>
            (entry.offers ?? []).map(offer => ({
              entry,
              offer,
              submitter: this.substitution.resolve(offer, this.salesIndex),
              commentDraft: '',
              // Comments start collapsed by default; the badge shows the count.
              commentsOpen: false,
            })));
          // NP20-924: Optionen aus der Sales-Liste, nicht nur aus den vorkommenden Kürzeln — sonst
          // fehlt, wer aktuell nur vertritt und selbst auf keinem Angebot steht. Dazu die Kürzel
          // der Angebote, deren Person nicht mehr in der Liste steht.
          this.submittedByOptions = salesAbbreviations(sales, this.rows.map(r => r.submitter.original));
          this.normalizeFilters(loadedSales !== null);
          this.buildFilterOptions();
          this.applyFilters();
          this.isLoading = false;
        },
        error: () => {
          this.isLoading = false;
          this.messageService.error('benchOverview.loadError');
        },
      });
  }

  onFilterChange(): void {
    // Adresse in der Reihenfolge der Optionsliste, nicht der Klickfolge.
    this.submittedByFilter = inOptionOrder(this.submittedByFilter, this.submittedByOptions);
    this.probabilityFilter = inOptionOrder(this.probabilityFilter, this.probabilityOptions);
    this.buFilter = inOptionOrder(this.buFilter, this.buFilterOptions);
    this.persistFilterState();
    this.applyFilters();
  }

  onSearchChange(): void {
    this.persistFilterState(true);
    this.applyFilters();
  }

  /** Die Adresszeile gewinnt, sonst greift die gemerkte Auswahl (NP20-984). */
  private restoreFilterState(): void {
    const params = this.route.snapshot.queryParamMap;
    const state = this.filterState.hasFilterParams(params)
      ? this.filterState.fromQueryParams(params)
      : this.filterState.read();
    this.submittedByFilter = state.submittedBy;
    this.probabilityFilter = state.probability;
    this.buFilter = state.bu;
    this.searchText = state.search;
    this.sortKey = state.sortKey;
    this.sortDir = state.sortDir;
    this.persistFilterState();
  }

  private currentFilterState(): SalesFilterState {
    return {
      submittedBy: [...this.submittedByFilter],
      probability: [...this.probabilityFilter],
      bu: [...this.buFilter],
      search: this.searchText,
      sortKey: this.sortKey,
      sortDir: this.sortDir,
    };
  }

  private persistFilterState(debounceUrl = false): void {
    const state = this.currentFilterState();
    this.filterState.write(state);
    window.clearTimeout(this.urlWriteTimer);
    if (!debounceUrl) {
      this.writeQueryParams(state);
      return;
    }
    this.urlWriteTimer = window.setTimeout(
      () => this.writeQueryParams(this.currentFilterState()),
      SalesViewComponent.URL_WRITE_DELAY_MS);
  }

  private writeQueryParams(state: SalesFilterState): void {
    replaceQueryParams(this.location, this.router, this.ownPath, this.filterState.toQueryParams(state));
  }

  /**
   * Veraltete BU-Ids und unbekannte Kürzel fallen still weg, der Rest bleibt — nur wenn die Optionen
   * wirklich geladen sind; sonst blieben gemerkte Werte stehen (NP20-984).
   */
  private normalizeFilters(salesLoaded: boolean): void {
    const before = this.filterState.toQueryParams(this.currentFilterState());
    if (this.businessUnits.length) {
      this.buFilter = this.businessUnits.map(bu => bu.id).filter(id => this.buFilter.includes(id));
    }
    if (salesLoaded) {
      this.submittedByFilter = this.submittedByOptions.filter(a => this.submittedByFilter.includes(a));
    }
    const after = this.filterState.toQueryParams(this.currentFilterState());
    if (before['bu'] !== after['bu'] || before['submittedBy'] !== after['submittedBy']) this.persistFilterState();
  }

  private buildFilterOptions(): void {
    this.submittedByFilterOptions = [...this.submittedByOptions];
    this.probabilityFilterOptions = [...this.probabilityOptions];
    this.buFilterOptions = this.businessUnits.map(bu => bu.id);
    const buLabels = new Map<string, string>();
    for (const bu of this.businessUnits) {
      buLabels.set(bu.id, `${bu.key} — ${bu.name}`);
    }
    this.buFilterLabels = buLabels;
  }

  private applyFilters(): void {
    const term = this.searchText.trim().toLowerCase();
    this.visibleRows = this.rows.filter(r => {
      // NP20-924: eigene Angebote der Person UND die, die sie gerade vertretungsweise betreut.
      if (this.submittedByFilter.length && !this.submittedByFilter.some(
        abbreviation => this.substitution.offerMatches(r.offer, abbreviation, this.salesIndex))) return false;
      if (this.probabilityFilter.length
        && !this.probabilityFilter.includes(String(r.offer.probability ?? ''))) return false;
      if (this.buFilter.length && !this.buFilter.includes(r.offer.businessUnitId ?? '')) return false;
      if (term && !this.matchesSearch(r, term)) return false;
      return true;
    });
    this.sortRows();
  }

  /**
   * NP20-798: Der Status geht als angezeigtes Label in die Suche, nicht als Enum-Wert. Vorher fand
   * „Offen" nichts, während „Open" alle offenen Angebote lieferte — gesucht wurde der interne Wert.
   */
  private statusLabel(status: RequestStatus | null | undefined): string {
    return status ? this.translate.instant('benchOverview.status.' + status.toLowerCase()) : '';
  }

  /** Free-text match over person, offer fields and the offer's comments (NP20-798). */
  private matchesSearch(row: SalesRow, term: string): boolean {
    const haystack: (string | null | undefined)[] = [
      row.entry.fullName,
      row.entry.abbreviation,
      row.entry.firm,
      row.offer.role,
      row.offer.customer,
      row.offer.intermediary,
      // NP20-924: Das aktive Kürzel zählt mit, damit Suche und Filter dieselben Angebote finden.
      row.submitter.original,
      row.submitter.active,
      row.offer.referenceNumber,
      row.offer.externalReferenceNumber,
      this.statusLabel(row.offer.status),
      ...(row.offer.notes ?? []).map(n => n.text),
    ];
    return haystack.some(value => (value ?? '').toString().toLowerCase().includes(term));
  }

  sortIndicator(key: string): string {
    return sortIndicatorFor(this.sortKey, key, this.sortDir);
  }

  onSort(key: string): void {
    if (this.sortKey === key) {
      this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortKey = key;
      this.sortDir = 'asc';
    }
    this.persistFilterState();
    this.sortRows();
  }

  private sortRows(): void {
    const dir = this.sortDir === 'asc' ? 1 : -1;
    this.visibleRows = [...this.visibleRows].sort((a, b) => {
      const va = this.sortValue(a);
      const vb = this.sortValue(b);
      if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * dir;
      return String(va).localeCompare(String(vb)) * dir;
    });
  }

  private sortValue(row: SalesRow): string | number {
    switch (this.sortKey) {
      case 'firm': return row.entry.firm ?? '';
      case 'role': return row.offer.role ?? '';
      case 'customer': return row.offer.customer ?? '';
      case 'intermediary': return row.offer.intermediary ?? '';
      // NP20-778: nach dem angezeigten Kuerzel sortieren. Vorher war es die interne
      // businessUnitId, deren Reihenfolge nichts mit der Anzeige zu tun hat.
      case 'bu': return this.buFor(row.offer)?.key ?? '';
      case 'conditions': return row.offer.hourlyRateRemote || row.offer.hourlyRateOnSite || 0;
      // NP20-924: nach der aktuell zuständigen Person sortieren, damit übernommene Angebote beim
      // Vertreter einsortieren und nicht bei der abwesenden Person.
      case 'submittedBy': return row.submitter.active;
      case 'probability': return row.offer.probability ?? -1;
      case 'extension': return row.offer.extension ? 1 : 0;
      case 'status': {
        // Unbekannter Status ans Ende sortieren (indexOf -> -1 wuerde sonst vorne landen)
        const idx = REQUEST_STATUSES.indexOf(row.offer.status);
        return idx === -1 ? 999 : idx;
      }
      case 'date': return row.offer.date ?? '';
      default: return row.entry.fullName ?? '';
    }
  }

  get offerCount(): number {
    return this.visibleRows.length;
  }

  getInitials(fullName: string): string {
    if (!fullName) return '';
    return fullName.trim().split(/\s+/).map(w => w[0] ?? '').join('').toUpperCase().slice(0, 2);
  }

  buFor(offer: OfferDTO): BusinessUnitDTO | null {
    return (offer.businessUnitId && this.buById.get(offer.businessUnitId)) || null;
  }

  /** Konditionen kompakt: „95 / 110 €/h". */
  conditionsLabel(offer: OfferDTO): string {
    const remote = offer.hourlyRateRemote;
    const onSite = offer.hourlyRateOnSite;
    if (!remote && !onSite) return '';
    if (remote && onSite) return `${remote} / ${onSite} €/h`;
    return `${remote || onSite} €/h`;
  }

  /** Appends the row's draft as a new comment on the offer and persists it. */
  addComment(row: SalesRow): void {
    const text = row.commentDraft.trim();
    if (!text || this.savingOffer) {
      return;
    }
    const offer = row.offer;
    const previousNotes = offer.notes;
    offer.notes = [...offer.notes, {id: null, date: null, author: '', text, isMeetingNote: false}];

    this.persistOffer(
      row,
      () => {
        row.commentDraft = '';
        this.messageService.success('salesView.comments.saved');
      },
      () => (offer.notes = previousNotes),
    );
  }

  askRemoveComment(row: SalesRow, note: NoteDTO): void {
    if (this.savingOffer) {
      return;
    }
    this.pendingCommentRemoval = {row, note};
  }

  cancelRemoveComment(): void {
    this.pendingCommentRemoval = null;
  }

  confirmRemoveComment(): void {
    const pending = this.pendingCommentRemoval;
    this.pendingCommentRemoval = null;
    if (!pending || this.savingOffer) {
      return;
    }
    const {row, note} = pending;
    const offer = row.offer;
    if (!offer.notes.includes(note)) {
      return;
    }
    const previousNotes = offer.notes;
    offer.notes = offer.notes.filter(n => n !== note);

    this.persistOffer(row, undefined, () => (offer.notes = previousNotes));
  }

  private persistOffer(row: SalesRow, onSuccess?: () => void, onError?: () => void): void {
    this.savingOffer = row.offer;
    const entryId = row.entry.id;
    this.benchService.updateEntry(entryId, row.entry)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: saved => {
          this.savingOffer = null;
          if (saved) this.applySavedEntry(saved);
          onSuccess?.();
        },
        error: (err: unknown) => {
          this.savingOffer = null;
          onError?.();
          if (err instanceof HttpErrorResponse && err.status === 409) {
            this.messageService.error('salesView.comments.saveConflict');
            this.benchService.getEntry(entryId)
              .pipe(takeUntilDestroyed(this.destroyRef))
              .subscribe({
                next: fresh => this.applySavedEntry(fresh),
                error: () => this.messageService.error('salesView.comments.saveError'),
              });
          } else {
            this.messageService.error('salesView.comments.saveError');
          }
        },
      });
  }

  /**
   * NP20-778: Den gespeicherten Eintrag übernehmen. Datum, Kürzel und id einer neuen Bemerkung
   * stempelt das Backend (stampNewNotes) und liefert sie in der Antwort — vorher wurde die Antwort
   * verworfen, sodass beides erst nach einem Neuladen erschien.
   *
   * Angebote haben keine stabile id, ihre Position in der Liste bleibt beim Speichern aber erhalten.
   * Deshalb wird über den Index zugeordnet, und zwar für alle Zeilen desselben Eintrags.
   */
  private applySavedEntry(saved: BenchEntryDTO): void {
    for (const candidate of this.rows) {
      if (candidate.entry.id !== saved.id) continue;
      const index = (candidate.entry.offers ?? []).indexOf(candidate.offer);
      const savedOffer = (saved.offers ?? [])[index];
      candidate.entry = saved;
      if (savedOffer) {
        candidate.offer = savedOffer;
        candidate.submitter = this.substitution.resolve(savedOffer, this.salesIndex);
      }
    }
    this.applyFilters();
  }

  /** Herkunft und Filter reisen in der Adresse mit, damit Zurück auch nach F5 hierher führt (NP20-1283). */
  openDetail(entry: BenchEntryDTO): void {
    const filters = Object.entries(this.filterState.toQueryParams(this.currentFilterState()))
      .filter(([, value]) => value !== null);
    this.router.navigate(['/berater-abfrage', entry.id], {queryParams: {from: 'sales', ...Object.fromEntries(filters)}});
  }

  backToList(): void {
    this.router.navigate(['/berater-abfrage']);
  }
}
